import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, CircleAlert, Clock, DollarSign, Send, Wand2 } from "lucide-react";
import { WaveMark } from "@/components/ui/WaveMark";

type WorkflowStatus = "draft" | "testing" | "active";

const SEED_CONVERSATION: { who: "flow" | "user"; text: string }[] = [
  { who: "flow", text: "Hey Nestor, what are we building today?" },
  { who: "user", text: "I need to automate referral submissions to Preferred IPA. Bastian kicks them off — I want Flow to pick them up every morning." },
  { who: "flow", text: "Got it. So every morning Flow checks AMD for new referrals from Bastian, then submits them to Preferred IPA. What time do you want it to run?" },
  { who: "user", text: "5:30 AM, same as the other eligibility jobs." },
  { who: "flow", text: "Same schedule. Good. Which portal are we hitting — Preferred IPA's Provider Portal or their legacy clearinghouse?" },
  { who: "user", text: "Provider Portal." },
  { who: "flow", text: "And it needs the SOAP note attached to each submission, right?" },
  { who: "user", text: "Yep." },
  { who: "flow", text: "Last thing — this is an irreversible submission. I'll pause for your explicit approval before clicking Submit on each one during the 20-run break-in, then move to 'approve batches' once we're confident. Sound right?" },
  { who: "user", text: "Yes, exactly." },
  { who: "flow", text: "Okay, I've got it drafted on the right. Take a look. You can edit any phase inline, or promote to Testing when it looks good." },
];

const SEED_PHASES = [
  { id: "P1", name: "Query AMD for new referrals", risk: "read" as const, detail: "Filter to Bastian, last 24h, status = pending outbound." },
  { id: "P2", name: "Open patient chart, extract details", risk: "read" as const, detail: "MRN, DOB, SOAP note — captured for the next phase." },
  { id: "P3", name: "Download SOAP note PDF", risk: "read" as const, detail: "Saved to job workspace. Wiped when job ends." },
  { id: "P4", name: "Authenticate to Preferred IPA", risk: "write" as const, detail: "TOTP MFA. Fails over to human after one retry." },
  { id: "P5", name: "Find patient by MRN/DOB", risk: "read" as const, detail: "Two close matches pause for disambiguation." },
  { id: "P6", name: "Fill referral form", risk: "write" as const, detail: "Specialist, CPT, reason, dates." },
  { id: "P7", name: "Attach SOAP note PDF", risk: "write" as const, detail: "Verify attachment appears before next phase." },
  { id: "P8", name: "Pause for approval", risk: "irreversible" as const, detail: "Screenshot + explanation surfaced to supervisor. Required during break-in." },
  { id: "P9", name: "Submit referral", risk: "irreversible" as const, detail: "Capture confirmation number. Failures page Nestor." },
  { id: "P10", name: "Log to AMD", risk: "write" as const, detail: "Write referral log entry + link confirmation number." },
  { id: "P11", name: "Cleanup + log out", risk: "read" as const, detail: "Delete downloaded PDFs; log out of portal; end runner session." },
];

interface Phase {
  id: string;
  name: string;
  risk: "read" | "write" | "irreversible";
  detail: string;
}

export function WizardScreen() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [conversation, setConversation] = useState(SEED_CONVERSATION);
  const [phases, setPhases] = useState<Phase[]>(SEED_PHASES);
  const [status, setStatus] = useState<WorkflowStatus>("draft");
  const [flash, setFlash] = useState<string | null>(null);
  const [thinking, setThinking] = useState(false);

  const send = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setConversation((c) => [...c, { who: "user", text: trimmed }]);
    setDraft("");
    setThinking(true);
    window.setTimeout(() => {
      setConversation((c) => [
        ...c,
        {
          who: "flow",
          text: "Got it — I've updated my understanding. Double-check the preview on the right and we can keep iterating from there.",
        },
      ]);
      setThinking(false);
    }, 600);
  };

  const addPhase = () => {
    const n = phases.length + 1;
    setPhases((p) => [
      ...p,
      {
        id: `P${n}`,
        name: "New phase",
        risk: "read",
        detail: "Click to describe what Flow should do here.",
      },
    ]);
    flashMessage(`Added phase P${n}. Describe what Flow should do — I'll refine it with you.`);
  };

  const flashMessage = (text: string) => {
    setFlash(text);
    window.setTimeout(() => setFlash((f) => (f === text ? null : f)), 3500);
  };

  const save = () => flashMessage("Draft saved. Workflow definitions are versioned in Azure SQL.");

  const promoteToTesting = () => {
    if (status === "active") {
      flashMessage("Already promoted to active. Edit and re-test to make changes.");
      return;
    }
    setStatus("testing");
    flashMessage(
      "Promoted to Testing. Flow will run 20 supervised break-in jobs before it can be activated.",
    );
  };

  const activate = () => {
    if (status !== "testing") {
      flashMessage("Complete the 20-run break-in before activating.");
      return;
    }
    setStatus("active");
    flashMessage(
      "Activated! Scheduled for 5:30 AM daily. Routing back to the dashboard — you'll see this workflow running tomorrow.",
    );
    window.setTimeout(() => navigate("/dashboard"), 1400);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex items-center gap-4 border-b border-paper-line bg-paper-0 px-6 py-3">
        <Link
          to="/dashboard"
          className="flex items-center gap-1 rounded-sm px-2 py-1 text-sm text-ink-2 hover:bg-paper-tint"
        >
          <ArrowLeft size={14} aria-hidden /> Back
        </Link>
        <div className="flex items-center gap-2">
          <WaveMark size={22} />
          <span className="text-md font-medium text-ink-1">New workflow</span>
          <span className="text-ink-5">·</span>
          <span className="text-sm text-ink-3">drafting with Flow</span>
          <StatusBadge status={status} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={save}
            className="rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-1.5 text-sm text-ink-1 hover:bg-paper-tint"
          >
            Save draft
          </button>
          <button
            type="button"
            onClick={promoteToTesting}
            disabled={status === "testing" || status === "active"}
            className="rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-1.5 text-sm text-ink-1 hover:bg-paper-tint disabled:opacity-40"
          >
            {status === "testing" ? "In testing" : "Ready to test"}
          </button>
          <button
            type="button"
            onClick={activate}
            disabled={status === "active"}
            className="rounded-sm border border-teal-600 bg-teal-500 px-3 py-1.5 text-sm text-paper-3 hover:bg-teal-600 disabled:opacity-40"
          >
            {status === "active" ? (
              <span className="flex items-center gap-1">
                <Check size={12} /> Active
              </span>
            ) : (
              "Activate"
            )}
          </button>
        </div>
      </header>

      {flash && (
        <div className="border-b border-teal-100 bg-status-success-bg px-6 py-2 text-xs text-teal-700">
          {flash}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[55fr_45fr] overflow-hidden">
        <section className="flex min-h-0 flex-col overflow-hidden border-r border-paper-line">
          <div className="flex-1 space-y-4 overflow-auto p-6">
            {conversation.map((m, i) => (
              <Bubble key={i} who={m.who} text={m.text} />
            ))}
            {thinking && (
              <div className="flex items-center gap-2 text-xs text-ink-3">
                <WaveMark size={22} />
                <span className="italic">thinking…</span>
              </div>
            )}
          </div>
          <div className="border-t border-paper-line bg-paper-2 p-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <div className="flex items-end gap-2">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={2}
                  placeholder="Ask Flow anything about this workflow…"
                  maxLength={4000}
                  className="flex-1 resize-none rounded-sm border border-paper-line-strong bg-paper-3 p-2 text-sm text-ink-1 placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="flex items-center gap-1 rounded-sm border border-teal-600 bg-teal-500 px-3 py-2 text-sm text-paper-3 hover:bg-teal-600 disabled:opacity-40"
                >
                  <Send size={12} /> Send
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="flex min-h-0 flex-col overflow-auto bg-paper-2 p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-medium text-ink-1">Preferred IPA Referral Submission</h2>
            <span className="rounded-pill bg-teal-50 px-2 py-0.5 text-xs text-teal-700">
              Scheduled 5:30 AM
            </span>
          </div>

          <div className="mt-4 grid grid-cols-[110px_1fr] gap-2 rounded-md border border-paper-line bg-paper-3 p-4 text-sm">
            <Row label="Trigger">AMD — Bastian's morning referrals, 5:30 AM daily</Row>
            <Row label="Scope">LAOSS</Row>
            <Row label="Credentials">Preferred IPA (TOTP MFA)</Row>
            <Row label="Output">Referral confirmation number logged to AMD</Row>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h3 className="text-md font-medium text-ink-1">Phases</h3>
              <span className="font-mono text-xs text-ink-4">{phases.length}</span>
            </div>
            <button
              type="button"
              onClick={addPhase}
              className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-ink-3 hover:bg-paper-tint"
            >
              <Wand2 size={12} /> Add phase
            </button>
          </div>

          <ul className="mt-2 space-y-2">
            {phases.map((p) => (
              <PhaseCard key={p.id} phase={p} />
            ))}
          </ul>

          <div className="mt-5 grid grid-cols-3 gap-3 rounded-md border border-paper-line bg-paper-3 p-4">
            <Estimate icon={<Clock size={14} />} value="~18 min" label="per run" />
            <Estimate icon={<DollarSign size={14} />} value="~$0.42" label="per run (GPT-5.4)" />
            <Estimate icon={<Wand2 size={14} />} value="3.5 hrs" label="saved daily" />
          </div>
        </section>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: WorkflowStatus }) {
  const cfg = {
    draft: { label: "Draft", cls: "bg-paper-tint text-ink-2" },
    testing: { label: "Break-in testing", cls: "bg-status-approval-bg text-status-approval" },
    active: { label: "Active", cls: "bg-status-success-bg text-status-success" },
  }[status];
  return (
    <span
      className={`ml-2 rounded-pill px-2 py-0.5 text-[11px] uppercase tracking-wider ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function Bubble({ who, text }: { who: "flow" | "user"; text: string }) {
  const isFlow = who === "flow";
  return (
    <div className={["flex gap-3", isFlow ? "" : "flex-row-reverse"].join(" ")}>
      {isFlow ? (
        <WaveMark size={32} />
      ) : (
        <div
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-500 text-xs font-medium text-paper-3"
        >
          NP
        </div>
      )}
      <div
        className={[
          "max-w-[480px] rounded-md p-3 text-sm",
          isFlow
            ? "border border-paper-line bg-paper-2 text-ink-1"
            : "border border-teal-600 bg-teal-500 text-paper-3",
        ].join(" ")}
      >
        {isFlow && <div className="mb-1 font-serif italic text-xs text-teal-500">Flow</div>}
        {text}
      </div>
    </div>
  );
}

function PhaseCard({ phase }: { phase: Phase }) {
  const riskColor = {
    read: "bg-status-success-bg text-status-success",
    write: "bg-status-approval-bg text-status-approval",
    irreversible: "bg-status-failed-bg text-status-failed",
  }[phase.risk];
  const borderColor = {
    read: "var(--status-success)",
    write: "var(--status-approval)",
    irreversible: "var(--status-failed)",
  }[phase.risk];
  return (
    <li
      className="grid grid-cols-[36px_1fr_auto] gap-2 rounded-md border border-paper-line bg-paper-3 p-3"
      style={{ borderLeft: `3px solid ${borderColor}` }}
    >
      <span className="font-mono text-xs text-ink-4">{phase.id}</span>
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink-1">{phase.name}</div>
        <div className="mt-0.5 text-xs text-ink-3">{phase.detail}</div>
        {phase.risk === "irreversible" && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-status-failed">
            <CircleAlert size={11} aria-hidden />
            requires explicit approval
          </div>
        )}
      </div>
      <span
        className={[
          "inline-flex h-[22px] items-center rounded-pill px-2 text-[10px] uppercase tracking-wider",
          riskColor,
        ].join(" ")}
      >
        {phase.risk}
      </span>
    </li>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className="text-[11px] uppercase tracking-wider text-ink-4">{label}</span>
      <span className="text-ink-1">{children}</span>
    </>
  );
}

function Estimate({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-ink-3">
        {icon}
        <span className="text-[11px] uppercase tracking-wider">{label.split(" ")[0]}</span>
      </div>
      <div className="mt-1 font-mono text-lg text-ink-1">{value}</div>
      <div className="text-[11px] text-ink-3">{label}</div>
    </div>
  );
}
